import { z } from 'zod';
import type { components } from '../api/generated/public.gen';

/**
 * Política de complejidad de contraseña. La FUENTE DE VERDAD es el backend (env var global del
 * lambda, expuesta por `GET /public/password-policy` — DEC-RBAC-052); el front la REFLEJA, no la
 * define. El hook `usePasswordPolicy()` la trae con cache y, ante endpoint caído o aún no
 * desplegado, cae a `PASSWORD_POLICY_FALLBACK` para que la pantalla NUNCA se rompa.
 *
 * DEC-RBAC-052: el tipo SE DERIVA del openapi generado. `sync-api-types` ya trae el schema
 * `PasswordPolicy` (respuesta de `GET /public/password-policy`) a `public.gen.ts`; se eliminó la
 * interface local temporal.
 */
export type PasswordPolicy = components['schemas']['PasswordPolicy'];

/**
 * Fallback hardcodeado = los valores que hoy aplica el pool Cognito (TenantOnboardingManager
 * default): 12+ con mayúscula, minúscula, número y símbolo. Es lo que el front usaba antes de que
 * la policy fuera dinámica, así que con el endpoint caído el comportamiento es idéntico al previo.
 */
export const PASSWORD_POLICY_FALLBACK: PasswordPolicy = {
    minLength: 12,
    requireSymbol: true,
    requireNumber: true,
    requireUppercase: true,
    requireLowercase: true,
};

/** Longitud mínima del fallback (compat: algún consumidor podía importar la constante suelta). */
export const PW_MIN = PASSWORD_POLICY_FALLBACK.minLength;

/** Regla individual del checklist en vivo. */
export interface PasswordRule {
    label: string;
    test: (v: string) => boolean;
}

/**
 * Arma el checklist en vivo (largo + las clases de caracteres que la policy exige). Solo incluye
 * las reglas habilitadas por la policy — si `requireSymbol` está apagado, no se pinta ese ítem.
 */
export function buildPasswordRules(policy: PasswordPolicy): PasswordRule[] {
    const rules: PasswordRule[] = [
        {
            label: `Al menos ${String(policy.minLength)} caracteres`,
            test: (v) => v.length >= policy.minLength,
        },
    ];
    if (policy.requireUppercase) {
        rules.push({ label: 'Una mayúscula (A-Z)', test: (v) => /[A-Z]/.test(v) });
    }
    if (policy.requireLowercase) {
        rules.push({ label: 'Una minúscula (a-z)', test: (v) => /[a-z]/.test(v) });
    }
    if (policy.requireNumber) {
        rules.push({ label: 'Un número (0-9)', test: (v) => /[0-9]/.test(v) });
    }
    if (policy.requireSymbol) {
        rules.push({ label: 'Un símbolo (!@#$…)', test: (v) => /[^A-Za-z0-9]/.test(v) });
    }
    return rules;
}

/**
 * Factory del schema zod de una contraseña nueva, derivado de la policy. Aplica `minLength` y un
 * refine por cada `require*` activo, con los mismos mensajes en español en todos los formularios
 * (NEW_PASSWORD, recuperación, cambio y self-register).
 */
export function buildPasswordSchema(policy: PasswordPolicy): z.ZodString {
    let schema = z.string().min(policy.minLength, `Mínimo ${String(policy.minLength)} caracteres`);
    if (policy.requireUppercase) schema = schema.regex(/[A-Z]/, 'Falta una mayúscula');
    if (policy.requireLowercase) schema = schema.regex(/[a-z]/, 'Falta una minúscula');
    if (policy.requireNumber) schema = schema.regex(/[0-9]/, 'Falta un número');
    if (policy.requireSymbol) schema = schema.regex(/[^A-Za-z0-9]/, 'Falta un símbolo');
    return schema;
}

/**
 * Reglas del checklist con el fallback. Export de compatibilidad para consumidores que no pasan por
 * el hook (p.ej. tests). Los forms reales usan `buildPasswordRules(policy)` con la policy del hook.
 */
export const PASSWORD_RULES: PasswordRule[] = buildPasswordRules(PASSWORD_POLICY_FALLBACK);

/**
 * Schema con el fallback. Export de compatibilidad: los forms migraron a `buildPasswordSchema` con
 * la policy del hook, pero se mantiene este export para no romper imports preexistentes.
 */
export const newPasswordSchema = buildPasswordSchema(PASSWORD_POLICY_FALLBACK);
